import { useMemo } from 'react';

import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useSeedLessonShellCache from '@/data/hook/lesson-progress/useSeedLessonShellCache';
import useStudentContinuity from '@/data/hook/student-dashboard/useStudentContinuity';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { studentDashboardQueries } from '@/data/query-keys/studentDashboardQueryKey';
import { LessonNameFormatter } from '@/utils/lesson/nameFormatterFactory';
import { getCourseLessonsHref } from '@/utils/course-progress/lessonsHref';
import { ROUTES } from '@/utils/routes';
import {
  COURSE_TRACK_PAGE_SIZE,
  CourseTrackLesson,
  toCourseTrackLesson,
} from '@/utils/student-dashboard/courseTrackLesson';

export type CourseTrackLessonWithHref = CourseTrackLesson & {
  href?: string;
};

type UseStudentCourseTrackParams = {
  lessonNameFormatter: LessonNameFormatter;
  courseProgressId?: number;
  klassId?: number;
  slug?: string;
};

export default function useStudentCourseTrack({
  lessonNameFormatter,
  courseProgressId,
  klassId,
  slug,
}: UseStudentCourseTrackParams) {
  const {
    lesson: currentLesson,
    isLoading: isLoadingContinuity,
    error: continuityError,
  } = useStudentContinuity({ courseProgressId, klassId, slug });

  const {
    results: lessonProgresses,
    isInitialLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    error,
  } = useInfiniteService({
    ...studentDashboardQueries.lessonProgressesInfinite({
      courseProgressId: courseProgressId ? [courseProgressId] : undefined,
      orderBy: 'scheduled_lesson_order',
      scheduledLessonOrderGt: currentLesson?.scheduledLessonOrder,
      pageSize: COURSE_TRACK_PAGE_SIZE,
    }),
    enabled: !!courseProgressId && !isLoadingContinuity,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  useSeedLessonShellCache({ courseProgressId, lessonProgresses });

  const lessons = useMemo<CourseTrackLessonWithHref[]>(
    () =>
      lessonProgresses.map(lessonProgress => {
        const lesson = toCourseTrackLesson(lessonProgress, lessonNameFormatter);

        return {
          ...lesson,
          href: slug
            ? ROUTES.COURSES.LESSON.INFO(slug, lesson.lessonId, klassId)
            : undefined,
        };
      }),
    [lessonProgresses, lessonNameFormatter, slug, klassId],
  );

  const unitsHref = getCourseLessonsHref({ slug, klassId });

  return {
    lessons,
    unitsHref,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: isLoadingContinuity || isInitialLoading,
    error: error ?? continuityError,
  };
}
